/**
 * WebSocket client for real-time messaging
 * Auto-reconnects with exponential backoff
 */

const WS_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3001')
  .replace(/^http/, 'ws');

type EventHandler = (data: any) => void;

class WebSocketClient {
  private ws: WebSocket | null = null;
  private listeners = new Map<string, Set<EventHandler>>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private userId: string | null = null;
  private token: string | undefined;
  private connected = false;

  connect(userId: string, token?: string) {
    this.userId = userId;
    this.token = token;
    this.doConnect();
  }

  disconnect() {
    this.userId = null;
    this.connected = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  on(event: string, handler: EventHandler): () => void {
    let handlers = this.listeners.get(event);
    if (!handlers) {
      handlers = new Set();
      this.listeners.set(event, handlers);
    }
    handlers.add(handler);
    return () => {
      handlers!.delete(handler);
      if (handlers!.size === 0) {
        this.listeners.delete(event);
      }
    };
  }

  send(type: string, payload: Record<string, any>) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, ...payload }));
    }
  }

  isConnected(): boolean {
    return this.connected;
  }

  private doConnect() {
    if (!this.userId) return;

    const isDevAuth = import.meta.env.VITE_AUTH_MODE === 'dev';
    const params = isDevAuth
      ? `devUserId=${encodeURIComponent(this.userId)}`
      : `token=${encodeURIComponent(this.token || '')}`;

    try {
      this.ws = new WebSocket(`${WS_URL}/api/ws?${params}`);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectDelay = 1000;
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'connected') {
          this.connected = true;
          this.emit('connected', data);
        } else if (data.type === 'pong') {
          // keepalive response, ignore
        } else {
          this.emit(data.type, data);
        }
      } catch {
        // ignore malformed messages
      }
    };

    this.ws.onclose = () => {
      this.connected = false;
      this.emit('disconnected', {});
      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      // onclose will fire after onerror
    };

    // Start keepalive ping
    this.startPing();
  }

  private scheduleReconnect() {
    if (!this.userId) return;
    if (this.reconnectTimer) return;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.doConnect();
    }, this.reconnectDelay);

    this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
  }

  private startPing() {
    const interval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      } else {
        clearInterval(interval);
      }
    }, 30000);
  }

  private emit(event: string, data: any) {
    const handlers = this.listeners.get(event);
    if (handlers) {
      for (const handler of handlers) {
        handler(data);
      }
    }
  }
}

export const wsClient = new WebSocketClient();
